import * as React from "react";
import { useCallback, useMemo } from "react";
import { makeVisualization, retrieveChartData } from "views/components/aggregationbuilder/AggregationBuilder";
import { DateType } from "views/logic/aggregationbuilder/Pivot";
import BarVisualizationConfig from "views/logic/aggregationbuilder/visualizations/BarVisualizationConfig";
import useChartData from "views/components/visualizations/useChartData";
import useEvents from "views/components/visualizations/useEvents";
import useChartLayoutSettingsWithCustomUnits from "views/components/visualizations/hooks/useChartLayoutSettingsWithCustomUnits";
import useBarChartDataSettingsWithCustomUnits from "views/components/visualizations/hooks/useBarChartDataSettingsWithCustomUnits";
import usePlotOnClickPopover from "views/components/visualizations/hooks/usePlotOnClickPopover";
import XYPlot from "../XYPlot";
const setChartColor = (chart, colors) => {
  var _a;
  return {
    marker: { color: colors.get((_a = chart.originalName) != null ? _a : chart.name) }
  };
};
const defineSingleDateBarWidth = (chartDataResult, config, timeRangeFrom, timeRangeTo) => {
  const barWidth = 0.03;
  const minXUnits = 30;
  if (config.rowPivots.length !== 1 || config.rowPivots[0].type !== DateType) {
    return chartDataResult;
  }
  return chartDataResult.map((data) => {
    var _a;
    if (((_a = data == null ? void 0 : data.x) == null ? void 0 : _a.length) === 1) {
      const timeRangeMS = new Date(timeRangeTo) - new Date(timeRangeFrom);
      const widthXUnits = timeRangeMS * barWidth;
      return {
        ...data,
        width: [Math.max(minXUnits, widthXUnits)]
      };
    }
    return data;
  });
};
const BarVisualization = makeVisualization(
  ({ config, data, effectiveTimerange, height, width }) => {
    var _a;
    const visualizationConfig = (_a = config.visualizationConfig) != null ? _a : BarVisualizationConfig.empty();
    const barmode = useMemo(
      () => visualizationConfig && visualizationConfig.barmode ? visualizationConfig.barmode : void 0,
      [visualizationConfig]
    );
    const getBarChartDataSettingsWithCustomUnits = useBarChartDataSettingsWithCustomUnits({
      config,
      barmode
    });
    const _seriesGenerator = useCallback(
      ({ type, name, labels, values, originalName, total, idx, fullPath }) => {
        var _a2;
        const opacity = (_a2 = visualizationConfig == null ? void 0 : visualizationConfig.opacity) != null ? _a2 : 1;
        return {
          type,
          name,
          x: labels,
          y: values,
          opacity,
          originalName,
          ...getBarChartDataSettingsWithCustomUnits({
            originalName,
            name,
            fullPath,
            values,
            idx,
            total,
            xAxisItemsLength: labels.length
          })
        };
      },
      [visualizationConfig == null ? void 0 : visualizationConfig.opacity, getBarChartDataSettingsWithCustomUnits]
    );
    const rows = useMemo(() => retrieveChartData(data), [data]);
    const _chartDataResult = useChartData(rows, {
      widgetConfig: config,
      chartType: "bar",
      generator: _seriesGenerator
    });
    const { eventChartData, shapes } = useEvents(config, data.events);
    const chartData = useMemo(() => {
      const chartDataResult = eventChartData ? [..._chartDataResult, eventChartData] : _chartDataResult;
      return defineSingleDateBarWidth(chartDataResult, config, effectiveTimerange == null ? void 0 : effectiveTimerange.from, effectiveTimerange == null ? void 0 : effectiveTimerange.to);
    }, [_chartDataResult, config, effectiveTimerange == null ? void 0 : effectiveTimerange.from, effectiveTimerange == null ? void 0 : effectiveTimerange.to, eventChartData]);
    const getChartLayoutSettingsWithCustomUnits = useChartLayoutSettingsWithCustomUnits({ config, chartData, barmode });
    const layout = useMemo(() => {
      var _a2;
      const _layouts = getChartLayoutSettingsWithCustomUnits();
      if (shapes) {
        _layouts.shapes = [...(_a2 = _layouts.shapes) != null ? _a2 : [], ...shapes];
      }
      if (barmode) {
        _layouts.barmode = barmode;
      }
      return _layouts;
    }, [shapes, barmode, getChartLayoutSettingsWithCustomUnits]);
    const { popover, initializeGraphDivRef, onChartClick } = usePlotOnClickPopover("bar", config);
    return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
      XYPlot,
      {
        config,
        axisType: visualizationConfig.axisType,
        chartData,
        effectiveTimerange,
        setChartColor,
        height,
        width,
        plotLayout: layout,
        onClickMarker: onChartClick,
        onInitialized: initializeGraphDivRef
      }
    ), popover);
  },
  "bar"
);
export default BarVisualization;
